import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  REFERENCE_MATERIAL_FILE_MAX_LENGTH,
  REFERENCE_MATERIAL_TEXT_MAX_LENGTH,
  REFERENCE_MATERIAL_TITLE_MAX_LENGTH,
  getReferenceMaterial,
} from '../../../api/referenceMaterialApi';
import { deleteChatFile, uploadChatFile } from '../../../api/chatApi';
import { useToast } from '../../../components/Toast';
import { MAX_STORAGE_FILE_SIZE_BYTES } from '../../../constants/fileLimits';
import {
  SUPPORTED_UPLOAD_ACCEPT,
  isClearlyBlockedUploadFile,
} from '../../../constants/fileTypes';
import { getReferenceFileTypeLabel } from '../../../utils/referenceMaterialFormat';
import LibraryBackfillCard from './LibraryBackfillCard';
import './ReferenceMaterialsSection.css';

export const REFERENCE_UPLOAD_SESSION_PREFIX = 'reference-library-upload:';

// 지난 채팅에서 가져온 자료는 처음 올린 날이 남아, 언제 쓴 파일인지 알아보게 날짜를 붙인다.
const formatDay = (iso) => {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('ko-KR', {
    ...(sameYear ? {} : { year: 'numeric' }),
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Seoul',
  });
};

const formatMeta = (material) => {
  const parts = [];
  if (material.origin === 'auto') {
    const day = formatDay(material.created_at);
    parts.push(day ? `채팅에서 자동으로 모음 · ${day}` : '채팅에서 자동으로 모음');
  }
  if (material.kind === 'file') {
    // 형식을 알 수 없으면(예: 서버 file_type "Unknown") 라벨을 숨긴다 — "UNKNOWN" 노출 방지
    const typeLabel = getReferenceFileTypeLabel(material);
    if (typeLabel) parts.push(typeLabel);
  } else {
    parts.push('텍스트');
  }
  if (material.char_count) {
    parts.push(`${material.char_count.toLocaleString()}자`);
  }
  if (material.usage_count > 0) {
    parts.push(`${material.usage_count}회 사용`);
  }
  return parts.join(' · ');
};

// 업로드 진행률은 전송 단계에서만 의미가 있고, 그 뒤엔 서버가 내용을 읽는 시간이라 % 를 숨긴다.
const formatReadingLabel = (progress) => {
  const value = Math.round(progress || 0);
  return value > 0 && value < 95 ? `내용을 읽고 있어요… ${value}%` : '내용을 읽고 있어요…';
};

const KindIcon = ({ kind }) => (
  <span aria-hidden="true" className={`refmat-icon refmat-icon--${kind}`}>
    {kind === 'file' ? (
      <svg fill="none" height="16" viewBox="0 0 24 24" width="16">
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" stroke="currentColor" strokeWidth="1.8" />
        <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    ) : (
      <svg fill="none" height="16" viewBox="0 0 24 24" width="16">
        <path d="M5 6h14M5 12h14M5 18h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
      </svg>
    )}
  </span>
);

/**
 * 참고 자료(내 자료함) 섹션 본문.
 *
 * - 무료: 텍스트 자료 3개. 파일 저장 버튼은 보이되 누르면 PRO 안내(업셀)로 이어진다.
 * - PRO : 텍스트+파일 500개. 파일은 채팅 업로드 파이프라인(HWP/PDF/이미지 OCR)을 그대로 재사용한 뒤
 *         자료함으로 복제 저장한다.
 * - 개인정보 경고는 입력 폼 바로 아래 한 줄로만 고정 노출한다 (교육부 AI 활용 지침).
 */
const ReferenceMaterialsSection = ({
  currentUser,
  materials,
  limits,
  isLoaded,
  loadError,
  onCreateText,
  onCreateFromFile,
  onUpdate,
  onDelete,
  onReload,
}) => {
  const { showToast } = useToast();
  const fileInputRef = useRef(null);

  const [isAdding, setIsAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  // 파일 자료 편집: 서버에서 받은 본문 원문(변경 여부 비교용)과 로딩 상태 ('idle' | 'loading' | 'loaded' | 'error')
  const [editOriginalContent, setEditOriginalContent] = useState('');
  const [editContentStatus, setEditContentStatus] = useState('idle');
  const editLoadSeqRef = useRef(0);
  const [deletingId, setDeletingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  // 파일 업로드 대기열: [{ id, name, status: 'waiting' | 'reading', progress }]
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [showProNotice, setShowProNotice] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const isUploadingRef = useRef(false);
  const dragCounterRef = useRef(0);

  // 자동으로 모은 자료는 직접 저장 한도에 세지 않는다 (서버가 오래된 것부터 정리).
  // PRO 500개는 직접 저장+자동 합산이라 전체를 보여주고, 무료는 직접 저장한 텍스트 개수/3 을 보여준다.
  const autoCount = materials.filter((material) => material.origin === 'auto').length;
  const total = materials.length - autoCount;
  const maxTotal = limits?.max_total ?? 3;
  const maxAuto = limits?.max_auto ?? 20;
  const fileAllowed = Boolean(limits?.file_allowed);
  const shownCount = fileAllowed ? materials.length : total;
  const isFull = total >= maxTotal;
  const isEmpty = isLoaded && materials.length === 0;
  const isUploading = uploadQueue.length > 0;

  const resetNewForm = () => {
    setNewTitle('');
    setNewContent('');
    setIsAdding(false);
  };

  const handleCreateText = async () => {
    const content = newContent.trim();
    if (!content || isSaving) return;
    setIsSaving(true);
    try {
      await onCreateText({ content, title: newTitle.trim() || undefined });
      resetNewForm();
      showToast('자료를 저장했어요. 채팅의 첨부 메뉴에서 골라 쓸 수 있어요', 'success');
    } catch (error) {
      if (error?.status === 409) setShowProNotice(!fileAllowed);
      showToast(error?.message || '자료를 저장하지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // 파일 저장이 가능한 상태인지 확인. 불가하면 안내를 띄우고 false.
  const ensureCanUploadFiles = useCallback(() => {
    if (!fileAllowed) {
      setShowProNotice(true);
      return false;
    }
    if (isUploadingRef.current) {
      showToast('지금 올리는 파일이 끝나면 다음 파일을 올릴 수 있어요', 'info');
      return false;
    }
    if (isFull) {
      showToast(`자료함이 가득 찼어요 (최대 ${maxTotal}개). 사용하지 않는 자료를 정리해주세요.`, 'error');
      return false;
    }
    return true;
  }, [fileAllowed, isFull, maxTotal, showToast]);

  const handlePickFile = () => {
    if (!ensureCanUploadFiles()) return;
    fileInputRef.current?.click();
  };

  /**
   * 파일들을 순서대로 자료함에 저장한다 (파일 선택 · 드래그앤드롭 공용 경로).
   * 채팅 업로드(추출/OCR) → 자료함 복제 → 임시 업로드 레코드 정리 순서이며,
   * 첫 실패에서 멈추고 인라인으로 이유를 보여준다.
   */
  const uploadFilesAsMaterials = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0 || !currentUser) return;
    if (!ensureCanUploadFiles()) return;

    const remaining = Math.max(0, maxTotal - total);
    const accepted = files.slice(0, remaining);
    const skippedCount = files.length - accepted.length;

    isUploadingRef.current = true;
    setUploadError(null);
    setUploadQueue(accepted.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      name: file.name,
      status: index === 0 ? 'reading' : 'waiting',
      progress: 0,
    })));

    const sessionId = `${REFERENCE_UPLOAD_SESSION_PREFIX}${currentUser.uid}`;
    let savedCount = 0;
    let failure = null;

    for (const file of accepted) {
      if (isClearlyBlockedUploadFile(file)) {
        failure = `${file.name}: 문서·이미지 파일만 올릴 수 있어요.`;
        break;
      }
      if (file.size > MAX_STORAGE_FILE_SIZE_BYTES) {
        failure = `${file.name}: 파일이 너무 커요 (최대 50MB).`;
        break;
      }

      let uploadedFileId = null;
      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await uploadChatFile(
          file,
          sessionId,
          currentUser.uid,
          (progress) => setUploadQueue((prev) => (
            prev.length > 0 ? [{ ...prev[0], progress }, ...prev.slice(1)] : prev
          )),
          'content_reference'
        );
        uploadedFileId = result?.file_id || null;
        if (!uploadedFileId) {
          throw new Error('파일 내용을 읽지 못했어요. 다른 파일로 시도해주세요.');
        }
        // eslint-disable-next-line no-await-in-loop
        await onCreateFromFile({ sourceFileId: uploadedFileId });
        savedCount += 1;
      } catch (error) {
        if (error?.status === 403) setShowProNotice(true);
        failure = `${file.name}: ${error?.message || '저장하지 못했어요. 다시 시도해주세요.'}`;
      } finally {
        // 자료함 복제본이 생겼으므로 임시 업로드 레코드는 정리한다 (best-effort).
        if (uploadedFileId) {
          Promise.resolve().then(() => deleteChatFile(uploadedFileId)).catch(() => {});
        }
      }
      if (failure) break;

      // 다음 파일로 넘어간다.
      setUploadQueue((prev) => {
        const rest = prev.slice(1);
        return rest.length > 0 ? [{ ...rest[0], status: 'reading', progress: 0 }, ...rest.slice(1)] : [];
      });
    }

    isUploadingRef.current = false;
    setUploadQueue([]);

    if (failure) {
      setUploadError(failure);
    } else if (skippedCount > 0) {
      setUploadError(`자료함 한도(${maxTotal}개)에 맞춰 ${savedCount}개만 저장했어요. ${skippedCount}개는 저장하지 않았어요.`);
    }
    if (savedCount > 0) {
      showToast(
        savedCount === 1
          ? '자료를 저장했어요. 채팅의 첨부 메뉴에서 골라 쓸 수 있어요'
          : `${savedCount}개 파일을 자료함에 저장했어요`,
        'success'
      );
    }
  }, [currentUser, ensureCanUploadFiles, maxTotal, onCreateFromFile, showToast, total]);

  const handleFileChange = useCallback((event) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    // 같은 파일을 연속 선택해도 change 가 뜨도록 초기화
    if (event.target) event.target.value = '';
    uploadFilesAsMaterials(files);
  }, [uploadFilesAsMaterials]);

  // --- 드래그앤드롭 (섹션 본문 전체) ---
  const hasDraggedFiles = (event) => {
    const types = event.dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes('Files');
  };

  const handleDragEnter = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragOver = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    // 무료 사용자도 drop 은 받아서 PRO 안내로 연결한다 ('none' 이면 drop 이벤트가 안 온다)
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;
    uploadFilesAsMaterials(files);
  };

  const closeEdit = () => {
    editLoadSeqRef.current += 1;
    setEditingId(null);
    setEditContentStatus('idle');
  };

  // 파일 자료는 목록에 preview(앞 120자)만 있으므로, 편집을 열 때 본문 전문을 따로 불러와 채운다.
  const loadEditContent = async (material) => {
    const seq = editLoadSeqRef.current + 1;
    editLoadSeqRef.current = seq;
    setEditContentStatus('loading');
    try {
      const data = await getReferenceMaterial(currentUser, material.id);
      if (editLoadSeqRef.current !== seq) return; // 그 사이 다른 항목을 열었거나 닫았다
      const content = typeof data?.material?.content === 'string' ? data.material.content : '';
      setEditContent(content);
      setEditOriginalContent(content);
      setEditContentStatus('loaded');
    } catch (_error) {
      if (editLoadSeqRef.current !== seq) return;
      setEditContentStatus('error');
    }
  };

  const startEdit = (material) => {
    setDeletingId(null);
    setEditingId(material.id);
    setEditTitle(material.title || '');
    setEditContent('');
    setEditOriginalContent('');
    if (material.kind === 'file') {
      loadEditContent(material);
    } else {
      editLoadSeqRef.current += 1;
      setEditContentStatus('idle');
    }
  };

  const handleUpdate = async (material) => {
    if (isSaving) return;
    const fields = {};
    const title = editTitle.trim();
    if (title !== (material.title || '')) fields.title = title;
    if (material.kind === 'text') {
      const content = editContent.trim();
      if (content) fields.content = content;
    } else if (editContentStatus === 'loaded') {
      // 파일 자료: 불러온 원문과 달라졌을 때만 본문을 보낸다 (제목만 고치면 요약을 다시 만들지 않는다)
      const content = editContent.trim();
      if (content && content !== editOriginalContent.trim()) fields.content = content;
    }
    if (Object.keys(fields).length === 0) {
      closeEdit();
      return;
    }
    setIsSaving(true);
    try {
      await onUpdate(material.id, fields);
      closeEdit();
      showToast('자료를 수정했어요', 'success');
    } catch (error) {
      showToast(error?.message || '자료를 수정하지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (materialId) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onDelete(materialId);
      setDeletingId(null);
      showToast('자료를 삭제했어요', 'success');
    } catch (error) {
      showToast(error?.message || '자료를 삭제하지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isLoaded) {
    return <div className="refmat-loading">자료함을 불러오는 중…</div>;
  }

  if (loadError && materials.length === 0) {
    return (
      <div className="refmat-error">
        <p>자료함을 불러오지 못했어요.</p>
        <button className="myprompts__btn-ghost" onClick={onReload} type="button">
          다시 시도
        </button>
      </div>
    );
  }

  const showChoices = isEmpty && !isAdding && !isUploading;
  const fileTypesHint = 'PDF·HWP·HWPX·DOCX·이미지';

  return (
    <div
      className={`refmat ${isDragOver ? 'refmat--dragover' : ''}`}
      data-testid="refmat-dropzone"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragOver && !showChoices && (
        <div aria-hidden="true" className="refmat-drop-overlay">
          <span className="refmat-drop-overlay__text">
            {fileAllowed ? '여기에 놓으면 자료함에 저장돼요' : 'PRO에서 파일을 보관할 수 있어요'}
          </span>
        </div>
      )}

      <div className="refmat-summary">
        <span className="refmat-summary__count">
          {shownCount}
          <span className="refmat-summary__max">/{maxTotal}</span>
        </span>
        <span className="refmat-summary__label">
          {fileAllowed ? '텍스트·파일 자료' : '직접 적은 텍스트 (무료)'}
        </span>
        {!fileAllowed && (
          <Link className="refmat-summary__upgrade" to="/subscription">
            PRO는 파일 포함 500개
          </Link>
        )}
      </div>
      {autoCount > 0 && (
        <p className="refmat-auto-note">
          {fileAllowed
            ? `채팅에서 자동으로 모은 자료 ${autoCount}개 · 가득 차면 오래 안 쓴 것부터 빠져요`
            : `채팅에서 자동으로 모은 파일 ${autoCount}/${maxAuto} · 텍스트와 따로 세고, 최근 ${maxAuto}개까지 남아요`}
        </p>
      )}
      <LibraryBackfillCard
        currentUser={currentUser}
        isPro={fileAllowed}
        maxAuto={maxAuto}
        onCollected={onReload}
      />

      <input
        accept={SUPPORTED_UPLOAD_ACCEPT || undefined}
        aria-label="참고 자료 파일 선택"
        hidden
        multiple
        onChange={handleFileChange}
        ref={fileInputRef}
        type="file"
      />

      {showChoices && (
        <div className="refmat-choices">
          <button
            className="refmat-choice"
            onClick={() => setIsAdding(true)}
            type="button"
          >
            <span aria-hidden="true" className="refmat-choice__icon">
              <svg fill="none" height="22" viewBox="0 0 24 24" width="22">
                <path d="M5 6h14M5 12h14M5 18h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
              </svg>
            </span>
            <span className="refmat-choice__title">텍스트로 적기</span>
            <span className="refmat-choice__desc">예: 우리 학교 생활규정, 수행평가 기준</span>
          </button>
          <button
            className={`refmat-choice refmat-choice--file ${fileAllowed ? '' : 'refmat-choice--locked'}`}
            data-testid="refmat-file-choice"
            onClick={handlePickFile}
            type="button"
          >
            <span aria-hidden="true" className="refmat-choice__icon refmat-choice__icon--file">
              <svg fill="none" height="22" viewBox="0 0 24 24" width="22">
                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" stroke="currentColor" strokeWidth="1.8" />
                <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.8" />
              </svg>
            </span>
            <span className="refmat-choice__title">
              파일 올리기
              {!fileAllowed && <span className="refmat-pro-tag">PRO</span>}
            </span>
            <span className="refmat-choice__desc">
              {fileTypesHint} 등
              <span className="refmat-choice__drophint"> · 끌어다 놓아도 돼요</span>
            </span>
          </button>
        </div>
      )}

      {isAdding ? (
        <div className="myprompts__form refmat-form">
          <input
            aria-label="자료 제목"
            className="myprompts__form-title"
            maxLength={REFERENCE_MATERIAL_TITLE_MAX_LENGTH}
            onChange={(event) => setNewTitle(event.target.value)}
            placeholder="제목 (비우면 첫 줄로 자동)"
            type="text"
            value={newTitle}
          />
          <textarea
            aria-label="자료 내용"
            className="myprompts__form-body refmat-form__body"
            maxLength={REFERENCE_MATERIAL_TEXT_MAX_LENGTH}
            onChange={(event) => setNewContent(event.target.value)}
            placeholder={'AI가 답할 때 기준으로 삼을 내용을 붙여넣어요\n예) 우리 학교 생활규정, 수행평가 기준, 학급 운영 원칙'}
            rows={6}
            value={newContent}
          />
          <div className="refmat-form__foot">
            <span className="refmat-form__count">
              {newContent.length.toLocaleString()}/{REFERENCE_MATERIAL_TEXT_MAX_LENGTH.toLocaleString()}
            </span>
            <div className="myprompts__form-actions">
              <button className="myprompts__btn-ghost" onClick={resetNewForm} type="button">
                취소
              </button>
              <button
                className="myprompts__btn-primary"
                disabled={!newContent.trim() || isSaving || isFull}
                onClick={handleCreateText}
                type="button"
              >
                {isSaving ? '저장 중…' : '저장하기'}
              </button>
            </div>
          </div>
        </div>
      ) : !showChoices && (
        <div className="refmat-actions">
          <button
            className="myprompts__add-btn refmat-add"
            disabled={isFull && fileAllowed}
            onClick={() => {
              if (isFull) {
                setShowProNotice(!fileAllowed);
                if (fileAllowed) {
                  showToast(`자료함이 가득 찼어요 (최대 ${maxTotal}개)`, 'error');
                }
                return;
              }
              setIsAdding(true);
            }}
            type="button"
          >
            + 텍스트 자료
          </button>
          <button
            className={`myprompts__add-btn refmat-add ${fileAllowed ? '' : 'refmat-add--locked'}`}
            disabled={isUploading}
            onClick={handlePickFile}
            type="button"
          >
            {isUploading ? '분석 중…' : '+ 파일 올리기'}
            {!fileAllowed && <span className="refmat-pro-tag">PRO</span>}
          </button>
        </div>
      )}

      {uploadError && (
        <div className="refmat-inline-error" role="alert">
          <span>{uploadError}</span>
          <button
            aria-label="안내 닫기"
            className="refmat-inline-error__close"
            onClick={() => setUploadError(null)}
            type="button"
          >
            닫기
          </button>
        </div>
      )}

      <p className="refmat-privacy">
        학생 이름·연락처 같은 개인정보는 넣지 마세요. 자료는 답변 참고에만 쓰이고 AI 학습에는 사용되지 않아요.
      </p>

      {showProNotice && !fileAllowed && (
        <div className="refmat-notice" role="status">
          <div className="refmat-notice__body">
            <strong>PRO에서는 한글·PDF 파일을 그대로 자료함에 보관해요</strong>
            <span>평가계획서·학교 지침 파일을 한 번 올려두고 대화마다 골라 첨부할 수 있어요.</span>
          </div>
          <div className="refmat-notice__actions">
            <button
              className="myprompts__btn-ghost"
              onClick={() => setShowProNotice(false)}
              type="button"
            >
              닫기
            </button>
            <Link className="refmat-notice__cta" to="/subscription">
              PRO 알아보기
            </Link>
          </div>
        </div>
      )}

      {(materials.length > 0 || isUploading) && (
        <ul className="myprompts__list refmat-list">
          {uploadQueue.map((pending) => (
            <li
              aria-busy="true"
              className="myprompts__item refmat-item refmat-item--pending"
              key={pending.id}
            >
              <KindIcon kind="file" />
              <div className="myprompts__item-main">
                <span className="myprompts__item-title">{pending.name}</span>
                <p className="myprompts__item-body refmat-item__meta">
                  {pending.status === 'reading' ? formatReadingLabel(pending.progress) : '대기 중'}
                </p>
                <span aria-hidden="true" className="refmat-skeleton" />
              </div>
            </li>
          ))}
          {materials.map((material) => {
            const isEditing = editingId === material.id;
            const isDeleting = deletingId === material.id;
            return (
              <li className="myprompts__item refmat-item" key={material.id}>
                {isEditing ? (
                  <div className="myprompts__form myprompts__form--edit">
                    <input
                      aria-label="자료 제목 수정"
                      className="myprompts__form-title"
                      maxLength={REFERENCE_MATERIAL_TITLE_MAX_LENGTH}
                      onChange={(event) => setEditTitle(event.target.value)}
                      type="text"
                      value={editTitle}
                    />
                    {material.kind === 'text' && (
                      <textarea
                        aria-label="자료 내용 수정"
                        className="myprompts__form-body refmat-form__body"
                        maxLength={REFERENCE_MATERIAL_TEXT_MAX_LENGTH}
                        onChange={(event) => setEditContent(event.target.value)}
                        placeholder="내용을 바꾸려면 새 내용을 전체 붙여넣어요 (비우면 제목만 수정)"
                        rows={5}
                        value={editContent}
                      />
                    )}
                    {material.kind === 'file' && editContentStatus === 'loading' && (
                      <div
                        aria-busy="true"
                        aria-label="본문을 불러오는 중"
                        className="refmat-form__skeleton"
                        role="status"
                      >
                        <span className="refmat-skeleton refmat-form__skeleton-line" />
                        <span className="refmat-skeleton refmat-form__skeleton-line" />
                        <span className="refmat-skeleton refmat-form__skeleton-line" />
                      </div>
                    )}
                    {material.kind === 'file' && editContentStatus === 'error' && (
                      <p className="refmat-form__hint" role="alert">
                        본문을 불러오지 못했어요. 지금은 제목만 바꿀 수 있어요.
                      </p>
                    )}
                    {material.kind === 'file' && editContentStatus === 'loaded' && (
                      <>
                        <textarea
                          aria-label="자료 내용 수정"
                          className="myprompts__form-body refmat-form__body"
                          maxLength={REFERENCE_MATERIAL_FILE_MAX_LENGTH}
                          onChange={(event) => setEditContent(event.target.value)}
                          rows={8}
                          value={editContent}
                        />
                        <div className="refmat-form__foot refmat-form__foot--hint">
                          <span className="refmat-form__hint">
                            파일에서 읽어낸 본문이에요. 잘못 읽힌 부분을 고치면 AI가 고친 내용을 참고해요.
                          </span>
                          <span className="refmat-form__count">
                            {editContent.length.toLocaleString()}/{REFERENCE_MATERIAL_FILE_MAX_LENGTH.toLocaleString()}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="myprompts__form-actions">
                      <button
                        className="myprompts__btn-ghost"
                        onClick={closeEdit}
                        type="button"
                      >
                        취소
                      </button>
                      <button
                        className="myprompts__btn-primary"
                        disabled={
                          isSaving
                          || editContentStatus === 'loading'
                          || (material.kind === 'file' && editContentStatus === 'loaded' && !editContent.trim())
                        }
                        onClick={() => handleUpdate(material)}
                        type="button"
                      >
                        {isSaving ? '저장 중…' : '저장'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <KindIcon kind={material.kind} />
                    <div className="myprompts__item-main">
                      <span className="myprompts__item-title">{material.title}</span>
                      {(material.digest_status === 'pending' || material.summary) && (
                        <p className="refmat-item__summary">
                          {material.digest_status === 'pending'
                            ? '내용을 읽고 있어요…'
                            : material.summary}
                        </p>
                      )}
                      <p className="myprompts__item-body refmat-item__meta">
                        {formatMeta(material)}
                        {material.kind === 'file' && material.source_filename && (
                          <> · {material.source_filename}</>
                        )}
                      </p>
                      {material.preview && (
                        <p className="myprompts__item-body refmat-item__preview">{material.preview}</p>
                      )}
                    </div>
                    <div className="myprompts__item-actions">
                      {isDeleting ? (
                        <>
                          <button
                            className="myprompts__btn-ghost"
                            onClick={() => setDeletingId(null)}
                            type="button"
                          >
                            취소
                          </button>
                          <button
                            className="myprompts__btn-danger"
                            disabled={isSaving}
                            onClick={() => handleDelete(material.id)}
                            type="button"
                          >
                            삭제 확인
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            aria-label={`${material.title} 수정`}
                            className="myprompts__btn-ghost"
                            onClick={() => startEdit(material)}
                            type="button"
                          >
                            수정
                          </button>
                          <button
                            aria-label={`${material.title} 삭제`}
                            className="myprompts__btn-ghost"
                            onClick={() => {
                              setEditingId(null);
                              setDeletingId(material.id);
                            }}
                            type="button"
                          >
                            삭제
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ReferenceMaterialsSection;
